/**
 * Where a browser error goes so that somebody sees it (PRD J5, NAV-11).
 *
 * This is the only one of `logError`'s destinations that reaches us.
 * `console.error` is a console in a browser we do not have, and
 * `Sentry.captureException` is a no-op: `VITE_SENTRY_DSN` is empty in every
 * environment file, so `Sentry.init` never runs with a DSN. This module writes
 * `client_errors/{id}`, and the `on_client_error_created` trigger republishes
 * it to Cloud Error Reporting, which groups it by stack trace and is where the
 * alert policy watches (ADR 0019).
 *
 * **The writer is injected rather than imported.** This module holds no
 * Firestore dependency of its own; `installClientErrorSink()` supplies one at
 * startup. That is what keeps `logError` — which nearly every hook in the app
 * calls — from pulling Firebase into the import graph of every test that
 * touches one, and it makes the rate limiting below testable without a mock.
 * Uninstalled, reporting is a no-op, which is the correct behaviour under test
 * and in Storybook.
 */

import i18next from "i18next";

/** Field ceilings. `firestore.rules` enforces the same numbers and
 *  `test_client_error_caps_sync.py` fails the build if the two disagree, which
 *  is what makes this comment a fact rather than a hope. Slicing to them here
 *  matters because the rules reject an over-long document *whole*: the report
 *  is lost, not truncated, and the sink swallows that refusal by design. */
export const CLIENT_ERROR_MAX_MESSAGE = 1000;
export const CLIENT_ERROR_MAX_STACK = 4000;
export const CLIENT_ERROR_MAX_NAME = 200;
export const CLIENT_ERROR_MAX_PAGE_PATH = 500;
export const CLIENT_ERROR_MAX_USER_AGENT = 500;
export const CLIENT_ERROR_MAX_LANGUAGE = 20;

/**
 * How many distinct errors one page load may report.
 *
 * Every signed-in account can write to `client_errors`, and a component that
 * throws on every render throws faster than anyone can navigate away. Without
 * a ceiling one broken screen is a write loop against a collection with no
 * other brake on it. Twenty distinct faults is far more than any single
 * session's worth of signal.
 */
export const CLIENT_ERROR_SESSION_LIMIT = 20;

export interface ClientErrorPayload {
  errorName: string;
  message: string;
  stack: string;
  pagePath: string;
  userAgent: string;
  language: string;
}

export type ClientErrorWriter = (payload: ClientErrorPayload) => Promise<void>;

let writer: ClientErrorWriter | null = null;
let reported = new Set<string>();

/** Install the real sink. Called once, at startup, by `index.tsx`. */
export function setClientErrorWriter(next: ClientErrorWriter): void {
  writer = next;
}

/** Forget this page load's history. Exported for tests; nothing else calls it. */
export function resetClientErrorSink(): void {
  writer = null;
  reported = new Set<string>();
}

/**
 * What makes two throws "the same error".
 *
 * Name plus message plus the topmost stack frame. Deliberately not the whole
 * stack: React re-renders the same fault from different depths, and a
 * fingerprint that included the full trace would treat each of those as new
 * and defeat the deduplication entirely. Deliberately not name-plus-message
 * alone either — one generic message ("Missing or insufficient permissions")
 * arises at genuinely different call sites, and collapsing those loses the
 * only thing that tells them apart.
 */
export function fingerprint(payload: ClientErrorPayload): string {
  const topFrame = payload.stack.split("\n").find((line) => line.includes("at "));
  return `${payload.errorName}|${payload.message}|${topFrame ?? ""}`;
}

/**
 * Report a browser error, at most once per distinct fault per page load.
 *
 * Never throws and never rejects: this runs inside `logError`, which runs
 * inside `catch` blocks, and a sink that can fail turns a handled error into
 * an unhandled one. It also never calls `logError` itself — the one failure
 * this module cannot report is its own.
 */
export async function reportClientError(error: unknown): Promise<void> {
  try {
    if (!writer) return;

    const payload = buildPayload(error);
    const key = fingerprint(payload);
    if (reported.has(key)) return;
    if (reported.size >= CLIENT_ERROR_SESSION_LIMIT) return;
    // Recorded before the await, not after: two synchronous throws of the same
    // fault would otherwise both pass the check and both write.
    reported.add(key);

    await writer(payload);
  } catch {
    // Swallowed on purpose, and silently. A console.warn here fires on every
    // signed-out page load — the write is refused by the rules, which is the
    // correct outcome, not a fault worth printing.
  }
}

export function buildPayload(error: unknown): ClientErrorPayload {
  const asError = error instanceof Error ? error : null;
  return {
    errorName: (asError?.name ?? typeof error).slice(0, CLIENT_ERROR_MAX_NAME),
    message: describe(error).slice(0, CLIENT_ERROR_MAX_MESSAGE),
    // A thrown non-Error has no stack. Empty is honest — Error Reporting then
    // groups on the message, which is all the information there is.
    stack: (asError?.stack ?? "").slice(0, CLIENT_ERROR_MAX_STACK),
    pagePath: pagePath(),
    userAgent: navigator.userAgent.slice(0, CLIENT_ERROR_MAX_USER_AGENT),
    // i18next's answer rather than the browser's, for the reason useReportBug
    // gives: a German session and an English one are different screens, and a
    // layout fault is often true in only one of them.
    language: (i18next.language || "").slice(0, CLIENT_ERROR_MAX_LANGUAGE),
  };
}

function describe(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  try {
    return JSON.stringify(error) ?? String(error);
  } catch {
    // Circular, or a getter that throws. `String()` cannot fail on either.
    return String(error);
  }
}

function pagePath(): string {
  const { pathname, search, hash } = window.location;
  return `${pathname}${search}${hash}`.slice(0, CLIENT_ERROR_MAX_PAGE_PATH);
}
